import { useCallback } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import { findingsApi, TicketsAccessLevel, TicketsAccessStatus } from '../../axios';

export interface TicketsAccessStatusState {
  access: TicketsAccessLevel;
  remainingTrialDays: number | null;
  checked: boolean;
  // True when `access` is the fail-open default rather than a real answer from
  // the backend. Callers that only read data can ignore it; callers about to
  // let the user *write* should not treat a guess as an entitlement.
  degraded: boolean;
  // Which backend serves the findings list. An allowlist, not a passthrough:
  // only the literal 'store' opts an account in, so a web deploy can land ahead
  // of the backend and a value nobody here understands keeps the Firestore path.
  searchBackend: 'store' | 'firestore';
  refresh: () => Promise<void>;
  applyStatus: (status: TicketsAccessStatus) => void;
}

export const TICKETS_ACCESS_QUERY_KEY = ['ticketsAccessStatus'];

// One shared React Query entry for the whole app, so the several surfaces that
// need to know whether Tickets is available (the module, the route guard, the
// main menu badge, the answer view, the checklist create affordance) each read
// it independently without any of them firing its own request or having it
// drilled down through props.
//
// @param enabled Pass false where no session-authenticated request should be
//   made at all -- e.g. a public, share-token webform.
export function useTicketsAccessStatus(enabled = true): TicketsAccessStatusState {
  const queryClient = useQueryClient();
  const { data, isError } = useQuery({
    queryKey: TICKETS_ACCESS_QUERY_KEY,
    queryFn: findingsApi.getTicketsAccessStatus,
    enabled,
    // A trial can lapse while the app is still open. Re-checking when the tab
    // regains focus closes the module on the user's next return instead of
    // waiting for a reload. This is React Query's default; stated explicitly
    // so a future change to the app-wide defaults can't silently disable the
    // only thing keeping a stale entitlement from outliving its trial.
    refetchOnWindowFocus: true,
  });

  // Deliberately NOT caught inside queryFn and turned into a `full` result:
  // that value is byte-identical to a real grant, so the app-wide 2-minute
  // staleTime would serve one failed request as an authoritative entitlement
  // to every surface, with nothing left to retry. Letting the query fail keeps
  // the failure visible (`degraded`), retryable, and out of the cache, while
  // the fallbacks below still fail open so an outage never locks a paying
  // account out of a module it owns.
  const failedToResolve = isError && data === undefined;

  // Lets a caller that already received a fresh status from another endpoint
  // (Findings.tsx, from POST /findings/start_trial's own response body) seed
  // the cache directly instead of paying for a second GET -- which also closes
  // the window where the UI still shows the pre-trial state.
  const applyStatus = useCallback(
    (status: TicketsAccessStatus) => {
      // Cancel first: a focus refetch already in flight would otherwise resolve
      // afterwards and overwrite this newer, POST-derived status with the
      // pre-trial one it started fetching.
      void queryClient.cancelQueries({ queryKey: TICKETS_ACCESS_QUERY_KEY });
      queryClient.setQueryData(TICKETS_ACCESS_QUERY_KEY, status);
    },
    [queryClient],
  );

  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: TICKETS_ACCESS_QUERY_KEY });
  }, [queryClient]);

  return {
    access: data?.access ?? 'full',
    remainingTrialDays: data?.remaining_trial_days ?? null,
    // Either a real answer or a settled failure counts as checked; both give
    // the caller something to act on. Still pending does not.
    checked: data !== undefined || failedToResolve,
    degraded: failedToResolve,
    searchBackend: data?.search_backend === 'store' ? 'store' : 'firestore',
    refresh,
    applyStatus,
  };
}

export interface CanUseTickets {
  canUseTickets: boolean;
  checked: boolean;
  degraded: boolean;
}

// The single question every non-Tickets surface actually asks: may this
// account use the module at all?
//
// Requires `checked`, which is what makes this safe to call without also
// handling the loading window at every call site. `access` defaults to 'full'
// before the request resolves, so answering from the value alone would report
// "yes" to every consumer during that window -- and one of them gates a button
// that writes a ticket straight to Firestore, past the backend's own gate.
// Allow-list on the value too: an unknown or renamed backend state reads as
// "no", never as "yes".
export function useCanUseTickets(enabled = true): CanUseTickets {
  const { access, checked, degraded } = useTicketsAccessStatus(enabled);

  return {
    canUseTickets: checked && (access === 'full' || access === 'trial_active'),
    checked,
    degraded,
  };
}
